/**
 * AuthProvider — login federado Google via Firebase + sessão real no BFF.
 *
 * Fluxo: Firebase autentica e devolve idToken -> BFF troca o idToken pelo
 * cookie HttpOnly __session -> GET /auth/me é a fonte de verdade sobre
 * quem é o usuário e qual o seu papel. Nada de token em localStorage.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { can, type CurrentSession, type LoginPayload, type Permission, type Role, type User } from '@dt-pm/core';
import { firebaseAuth, hasFirebaseConfig } from '@/shared/lib/firebase';
import { api, UNAUTHORIZED_EVENT } from '@/shared/lib/api';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account', hd: 'ipnet.cloud' });

function isOrganizationEmail(email: string | null | undefined): boolean {
  return Boolean(email?.trim().toLowerCase().endsWith('@ipnet.cloud'));
}

/** GET /auth/me devolve GlobalRole do BFF (SUPER_ADMIN | ADMIN | USER). PM é papel por projeto. */
function mapSessionRole(role: string): Role {
  if (role === 'SUPER_ADMIN') {
    return 'SUPER_ADMIN';
  }

  return role === 'ADMIN' ? 'ADMIN' : 'MEMBER';
}

function mapCurrentSession(session: CurrentSession): User {
  return {
    id: session.userId,
    name: session.name ?? session.email.split('@')[0] ?? 'Usuário',
    email: session.email,
    role: mapSessionRole(session.role),
    status: 'ACTIVE',
    avatarUrl: session.photoUrl ?? null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadCurrentSession = useCallback(async () => {
    try {
      const session = await api.auth.me();
      const photoURL = session.photoUrl ?? firebaseAuth?.currentUser?.photoURL ?? null;
      setUser({ ...mapCurrentSession(session), avatarUrl: photoURL });
    } catch (error) {
      setUser(null);
      throw new Error('Não foi possível confirmar sua sessão. Tente entrar novamente.', {
        cause: error,
      });
    }
  }, []);

  useEffect(() => {
    // Ausência de sessão ao abrir o app é esperada; após o login, a falha deve ser exibida.
    void Promise.resolve()
      .then(loadCurrentSession)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [loadCurrentSession]);

  // Quando o Firebase restaura a sessão do provedor federado, a photoURL passa a
  // existir; sincroniza o avatar sem refazer a chamada ao BFF.
  useEffect(() => {
    if (!firebaseAuth) return;
    return onAuthStateChanged(firebaseAuth, (fbUser) => {
      setUser((current) => {
        if (!current) return current;
        const photoURL = current.avatarUrl || fbUser?.photoURL || null;
        return current.avatarUrl !== photoURL ? { ...current, avatarUrl: photoURL } : current;
      });
    });
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => setUser(null);
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  const loginWithGoogle = useCallback(async () => {
    if (!hasFirebaseConfig || !firebaseAuth) {
      throw new Error('Firebase não configurado — defina VITE_FIREBASE_* em apps/web/.env.local.');
    }

    const result = await signInWithPopup(firebaseAuth, googleProvider);
    if (!isOrganizationEmail(result.user.email)) {
      await firebaseSignOut(firebaseAuth).catch(() => {});
      throw new Error('Só contas @ipnet.cloud podem acessar o Wave.');
    }
    try {
      const idToken = await result.user.getIdToken();
      await api.auth.sessionLogin(idToken);
      await loadCurrentSession();
    } catch (error) {
      await firebaseSignOut(firebaseAuth).catch(() => {});
      throw error;
    }
  }, [loadCurrentSession]);

  const login = useCallback(
    async (payload: LoginPayload) => {
      await api.auth.login(payload);
      await loadCurrentSession();
    },
    [loadCurrentSession],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // sessão já pode estar expirada no servidor; segue com a limpeza local.
    }
    if (hasFirebaseConfig && firebaseAuth) {
      await firebaseSignOut(firebaseAuth).catch(() => {});
    }
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      login,
      loginWithGoogle,
      logout,
      hasPermission: (permission) => can(user, permission),
    }),
    [user, isLoading, login, loginWithGoogle, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return context;
}
